// src/views/ExportQuestions.js
import React, { useState, useEffect } from 'react';
import { questionsAPI, plateformesAPI, formationsAPI } from '../api/api';

function ExportQuestions() {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [plateformes, setPlateformes] = useState([]);
  const [formations, setFormations] = useState([]);
  const [selectedPlateforme, setSelectedPlateforme] = useState('all'); // 'all' pour toutes les questions
  const [selectedFormation, setSelectedFormation] = useState('all'); // 'all' pour toutes les formations

  // Charger les plateformes et formations au montage du composant
  useEffect(() => {
    loadPlateformes();
    loadFormations();
  }, []);

  const loadPlateformes = async () => {
    try {
      const plateformesData = await plateformesAPI.getAll();
      setPlateformes(plateformesData);
    } catch (error) {
      console.error('Erreur lors du chargement des plateformes:', error);
    }
  };

  const loadFormations = async () => {
    try {
      const formationsData = await formationsAPI.getAll();
      setFormations(formationsData);
    } catch (error) {
      console.error('Erreur lors du chargement des formations:', error);
    }
  };

  const getExportButtonText = () => {
    if (selectedPlateforme !== 'all' && selectedFormation !== 'all') {
      const pf = plateformes.find(p => p.id === parseInt(selectedPlateforme));
      const f = formations.find(form => form.id === parseInt(selectedFormation));
      return `Exporter : ${pf?.nom} - ${f?.nom}`;
    }
    if (selectedPlateforme !== 'all') {
      const pf = plateformes.find(p => p.id === parseInt(selectedPlateforme));
      return `Exporter les questions de ${pf?.nom}`;
    }
    if (selectedFormation !== 'all') {
      const f = formations.find(form => form.id === parseInt(selectedFormation));
      return `Exporter les questions de ${f?.nom}`;
    }
    return 'Exporter toutes les questions';
  };

  const handleExport = async () => {
    setLoading(true);
    setError('');
    setMessage('');

    try {
      // Construire les paramètres d'export
      const params = {};
      if (selectedPlateforme !== 'all') {
        params.plateforme_id = selectedPlateforme;
      }
      if (selectedFormation !== 'all') {
        params.formation_id = selectedFormation;
      }

      // Télécharger le fichier CSV
      const blob = await questionsAPI.exportAll(params);

      // Construire le nom de fichier
      const filenameParts = ['questions_export'];
      if (selectedPlateforme !== 'all') {
        filenameParts.push(plateformes.find(pf => pf.id === parseInt(selectedPlateforme))?.nom || 'plateforme');
      }
      if (selectedFormation !== 'all') {
        filenameParts.push(formations.find(f => f.id === parseInt(selectedFormation))?.nom || 'formation');
      }
      const filename = filenameParts.join('_') + '.csv';

      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();

      // Nettoyer
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setMessage('Export terminé avec succès ! Le fichier a été téléchargé.');
    } catch (err) {
      console.error('Erreur lors de l\'export:', err);
      setError('Erreur lors de l\'export des questions: ' + (err.message || 'Erreur inconnue'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Exportation des questions</h2>

          <div className="mb-6">
            <p className="text-gray-600">
              Exportez les questions de la base de données au format CSV. Le fichier contiendra 
              toutes les informations relatives aux questions, y compris les réponses, les plateformes, 
              les formations et les modules associés.
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sélectionner une plateforme (facultatif)
            </label>
            <select
              value={selectedPlateforme}
              onChange={(e) => setSelectedPlateforme(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Toutes les plateformes</option>
              {plateformes.map((plateforme) => (
                <option key={plateforme.id} value={plateforme.id}>
                  {plateforme.nom}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sélectionner une formation (facultatif)
            </label>
            <select
              value={selectedFormation}
              onChange={(e) => setSelectedFormation(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Toutes les formations</option>
              {formations.map((formation) => (
                <option key={formation.id} value={formation.id}>
                  {formation.nom}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <button
              onClick={handleExport}
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition disabled:opacity-50"
            >
              {loading ? 'Export en cours...' : getExportButtonText()}
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
              {error}
            </div>
          )}

          {message && (
            <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-md">
              {message}
            </div>
          )}

          <div className="mt-8">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Format du fichier exporté</h3>
            <div className="bg-gray-50 p-4 rounded-md">
              <p className="font-mono text-sm mb-2">
                intitule,feedback,type_reponse,statut,donnees_dynamiques,annee_reglementaire,quiz_id,plateformes,formations_noms,domaines_expertise_noms,reponse1,reponse1_correct,reponse2,reponse2_correct,reponse3,reponse3_correct,reponse4,reponse4_correct
              </p>
              <p className="text-sm text-gray-600">
                "Quelle est la capitale de la France?","Paris est la capitale de la France","QCM","publié","false","2023","","Axelerance Qweed;Axilos Qweed","Formation 1","Domaine 1","Paris","1","Lyon","0","Marseille","0","Toulouse","0"
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExportQuestions;